import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { addChampionshipTeams } from 'modules/championship/store/championship/actions';
import { SportType } from 'modules/core/domain/enums/sport-type';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { getChampionshipTeamsV2 } from 'modules/teams/store/championship-teams/actions';
import { useLazyListTeamsQuery } from 'modules/teams/store/teams-api';
import { TeamOption, TeamSelectOption } from 'modules/teams/ui/components/team-async-select';
import { useAppDispatch } from 'store/hooks';
import { UDAsyncSelect } from 'ud-form/components/select/component';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';

/** Опция выдачи: уже заявленную команду react-select покажет, но выбрать не даст. */
type TeamModalOption = TeamSelectOption & { isDisabled?: boolean };

/** Сколько команд показывать в выдаче — остальные пользователь дофильтрует вводом. */
const SEARCH_LIMIT = 10;

/**
 * С какой длины ввода искать. По одному-двум символам совпадений столько, что выдача
 * бесполезна, а запрос уходит на каждое нажатие — поэтому ждём осмысленный кусок названия.
 */
const MIN_SEARCH_LENGTH = 3;

interface AddTeamsModalProps {
  championshipId: number;
  /** Команды, уже заявленные в этот турнир: в выдаче они видны, но выбрать их нельзя. */
  existTeamsId: number[];
  onClose: () => void;
}

/**
 * Добавление команд в заявки, второй флоу (v2).
 *
 * Отличий от формы старого флоу (`add-team-application-form`) три, и все три — про то,
 * что в v1 пользователь узнать не мог:
 *
 * - список команд организации виден **сразу при открытии**, без ввода: в v1 селект
 *   встречал «Нет совпадений», потому что `defaultOptions` никто не передавал;
 * - уже заявленная команда показана, но отключена с пометкой — в v1 проп `existTeamsId`
 *   объявлен и не использован, так что команду можно было «добавить» повторно;
 * - команд можно выбрать несколько за раз.
 *
 * Добавление — одна мутация на все выбранные команды (`addChampionshipTeams`), поэтому
 * частичного успеха не бывает: либо добавились все, либо ни одной и окно остаётся открытым
 * с прежним выбором. Список после мутации перечитывается с сервера — локальной дорисовки
 * состава, как в v1, здесь нет.
 *
 * Монтируется только на время показа: выдача грузится при открытии, а закрытие сбрасывает
 * выбор и кэш опций — иначе только что добавленная команда осталась бы в списке доступных.
 */
const AddTeamsModal = ({ championshipId, existTeamsId, onClose }: AddTeamsModalProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { info } = useOrganizer();
  const [searchTeams] = useLazyListTeamsQuery();

  const [selected, setSelected] = useState<readonly TeamModalOption[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const announcedIds = useMemo(() => new Set(existTeamsId), [existTeamsId]);

  const loadOptions = useCallback(
    async (inputValue: string) => {
      if (!info?.id) return [];

      // `true` — preferCacheValue: повторное открытие модалки с тем же вводом не идёт в сеть.
      const teams = await searchTeams(
        {
          tournamentId: info.id,
          limit: SEARCH_LIMIT,
          offset: 0,
          sportType: info.sportType ?? SportType.football,
          namePart: inputValue,
        },
        true,
      ).unwrap();

      return teams.map((team) => {
        const isAnnounced = announcedIds.has(team.id);

        return {
          value: team.id,
          data: team,
          isDisabled: isAnnounced,
          label: (
            <span className="flex w-full items-center justify-between">
              <TeamOption team={team} />
              {isAnnounced && (
                <span className="text-surfaceRomanSilver30 ml-4 shrink-0">{t('applications.v2.addModal.alreadyAdded')}</span>
              )}
            </span>
          ),
        } as TeamModalOption;
      });
    },
    [info, announcedIds, t, searchTeams],
  );

  const close = useCallback(() => {
    setSelected([]);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    const chosen = selected.map((option) => option.value);
    setIsSubmitting(true);

    try {
      await dispatch(addChampionshipTeams({ teamIds: chosen, championshipId })).unwrap();
      // Список v2 читается с сервера (`teams` + `championshipApplications`), локальной
      // дорисовки в редьюсере у него нет — без перечитывания новых команд на экране не будет.
      await dispatch(getChampionshipTeamsV2({ championshipId }));
    } catch {
      toast.error(t('applications.v2.addModal.error'));
      return;
    } finally {
      setIsSubmitting(false);
    }

    toast.success(t('applications.v2.addModal.success', { count: chosen.length }));
    close();
    // Одну команду добавляют, чтобы тут же заявить её игроков — ведём внутрь.
    if (chosen.length === 1) navigate(String(chosen[0]));
  }, [selected, dispatch, championshipId, t, close, navigate]);

  return (
    <Modal show onClose={close} size="md" noOverflow>
      <Modal.Header title={t('applications.v2.addModal.title')} subtitle={t('applications.v2.addModal.subtitle')} />

      <Modal.Body>
        <UDAsyncSelect<TeamModalOption, true>
          isMulti
          preloadOptions
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          emptyInputMessage={t('applications.v2.addModal.emptyInput')}
          minSearchLength={MIN_SEARCH_LENGTH}
          minSearchMessage={t('applications.v2.addModal.minSearch', { min: MIN_SEARCH_LENGTH })}
          placeholder={t('applications.v2.addModal.placeholder')}
          loadOptions={loadOptions}
          value={selected}
          onChange={(options) => setSelected(options ?? [])}
        />
      </Modal.Body>

      <Modal.Footer>
        <UDButton variant="secondary" onClick={close} disabled={isSubmitting}>
          {t('applications.v2.addModal.cancel')}
        </UDButton>
        <UDButton variant="primary" onClick={handleSubmit} loading={isSubmitting} disabled={selected.length === 0}>
          {t('applications.v2.addModal.submit')}
        </UDButton>
      </Modal.Footer>
    </Modal>
  );
};

export default AddTeamsModal;
